// utils/deliveryPhone.js
// ─────────────────────────────────────────────────────────────────────────────
// The contact phone used for delivery on checkout screens.
//
// Plan (first non-empty wins):
//   1. the customer's ACCOUNT phone (phone-OTP signups always have one);
//   2. the LAST delivery phone they used (persisted here) — covers Google/email
//      signups that have no account phone, and repeat customers;
//   3. a phone attached to a chosen saved address (handled by the screen);
//   4. otherwise empty — they type it, and we persist it for next time.
//
// Always editable on the screen; persistence is per-country (mirrors how
// userLocation is scoped) so a UG number doesn't leak into a ZM session.
// ─────────────────────────────────────────────────────────────────────────────
import { COUNTRY } from "../config/market";

const KEY = `lusuku:lastDeliveryPhone:${COUNTRY}`;

/** The phone on the user's account, tolerating the various auth shapes. */
export function accountPhone(currentUser) {
  return (
    currentUser?.phoneNumber ||
    currentUser?.result?.phoneNumber ||
    currentUser?.phone ||
    currentUser?.result?.phone ||
    ""
  );
}

/** The last delivery phone this browser used (persisted), or "". */
export function loadLastDeliveryPhone() {
  try {
    return localStorage.getItem(KEY) || "";
  } catch {
    return "";
  }
}

/** Persist the delivery phone the customer entered (best-effort). */
export function saveLastDeliveryPhone(phone) {
  try {
    const v = String(phone || "").trim();
    if (v.length >= 9) localStorage.setItem(KEY, v);
  } catch {
    /* non-fatal — persistence is a convenience, not a requirement */
  }
}

/**
 * Resolve the initial phone to prefill: account first, else last-used.
 * @returns {string}
 */
export function resolveInitialDeliveryPhone(currentUser) {
  return accountPhone(currentUser) || loadLastDeliveryPhone() || "";
}

// ── Payer (mobile-money) prefill ─────────────────────────────────────────────
// The checkout's mobile-money field wants a LOCAL 10-digit number (07XXXXXXXX
// in UG, 09/07XXXXXXXX in ZM) while the delivery contact may be stored in any
// of +2567…, 2567…, 07… shapes. Most customers pay with the same number we
// deliver to, so seeding the payer field from it removes retyping for the
// common case; the field stays fully editable for "someone else is paying".

/** Normalise any phone shape to the local 10-digit form, or "" if unclear. */
export function localizePayerPhone(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  // International with a 3-digit country code (2567…, 2607…): 12 digits total.
  if (digits.length === 12 && (digits.startsWith("256") || digits.startsWith("260"))) {
    return "0" + digits.slice(3);
  }
  // Already local: 07XXXXXXXX / 09XXXXXXXX.
  if (digits.length === 10 && digits.startsWith("0")) return digits;
  // Local with the leading 0 dropped: 7XXXXXXXX / 9XXXXXXXX.
  if (digits.length === 9 && (digits.startsWith("7") || digits.startsWith("9"))) {
    return "0" + digits;
  }
  return ""; // unrecognised shape: better no prefill than a wrong charge target
}

/**
 * The number to seed the mobile-money payer field with:
 * the delivery contact first (it is on screen and freshest), else the
 * account phone. Never authoritative; the customer can always change it.
 */
export function resolveInitialPayerPhone(shippingAddress, currentUser) {
  return (
    localizePayerPhone(shippingAddress?.customerNumber) ||
    localizePayerPhone(accountPhone(currentUser)) ||
    ""
  );
}

const deliveryPhone = {
  accountPhone,
  loadLastDeliveryPhone,
  saveLastDeliveryPhone,
  resolveInitialDeliveryPhone,
  localizePayerPhone,
  resolveInitialPayerPhone,
};
export default deliveryPhone;
